/**
 * F-0713 slice 8 (ADR-187, `docs/concept-work-area-access-requests.md` §5): who may answer a work
 * area's requests for agent access.
 *
 * A typed column of the work area, `projects.access_request_approvers`, with a CHECK on these values
 * (D-0125 chose the same shape for its switch). Anything absent or unknown reads as the default, so
 * an older or hand-written row never lets an agent answer.
 *
 * ⛔ Writing it asks `access_request.allow_agents`, never `work_area.manage` (AC-SV15), and only its own
 * mutation writes it; the general work-area update refuses it.
 */
export const ACCESS_REQUEST_APPROVERS = ['people', 'people-and-agents'] as const;
export type AccessRequestApproversType = (typeof ACCESS_REQUEST_APPROVERS)[number];

/** Only people answer, until an Owner says otherwise (D-0411 a). */
export const ACCESS_REQUEST_APPROVERS_DEFAULT: AccessRequestApproversType = 'people';

/** The role an agent holds to answer: bound to a named agent by a person, by no default role. */
export const ACCESS_APPROVER_ROLE_KEY = 'access-request-approver';

/** The stored value, read: only an exact known value counts; the rest read as people only. */
export function readAccessRequestApprovers(raw: unknown): AccessRequestApproversType {
  return (ACCESS_REQUEST_APPROVERS as readonly unknown[]).includes(raw)
    ? (raw as AccessRequestApproversType)
    : ACCESS_REQUEST_APPROVERS_DEFAULT;
}

/** Whether agents may answer under this value. */
export function agentsMayAnswer(value: AccessRequestApproversType): boolean {
  return value === 'people-and-agents';
}

/** `GET/PUT /work-areas/:id/access-request-approvers` (§5.1). */
export interface IAccessRequestApprovers {
  value: AccessRequestApproversType;
  /** Whether the reader holds `access_request.allow_agents` here; the control is drawn disabled without it. */
  mayChange: boolean;
  /** The Enterprise edition offers it (D-0229, D-0295); the control is not drawn in Community. */
  offered: boolean;
}

/** An agent holding the approver role here (§7.3), for Settings → Agents. */
export interface IAccessApproverAgent {
  agentId: string;
  agentName: string;
  ownerName: string;
  /** ISO time the role ends, or null for no end. */
  endsAt: string | null;
  /** False while the setting is `people` (the role is inert, not deleted), and after its end. */
  active: boolean;
}

/** `POST /work-areas/:id/access-approvers` (§7.3): give an agent the approver role here. */
export interface IAccessApproverGrantInput {
  agentId: string;
  /** Days until the role ends, or null for no end (D-0413: an agent's grant needs none). */
  days: number | null;
  /** The step-up proof, as for the general grant. */
  reauthToken?: string;
}

/** `GET /work-areas/:id/access-approvers`. */
export interface IAccessApproversList {
  approvers: IAccessApproverAgent[];
}
